import { touchevents } from 'modernizr';
import { clamp, debounce, get } from 'lodash';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl';
import { MAP_CONFIG, MAP_SOURCE_CONFIG, MAP_LAYER_CONFIG } from './data';


mapboxgl.accessToken = globals.config.mapbox_access_token;

let $emit;
let map;
let popupBase;
let popupActive;


function getPitchFromZoom(zoom) {
    const currentZoom = zoom || map.getZoom();
    const BASE_ZOOM = 14;
    const MAX_ZOOM = 17;
    const MIN_PITCH = 0;
    const MAX_PITCH = 45;

    const result = MAX_PITCH * (currentZoom - BASE_ZOOM) / (MAX_ZOOM - BASE_ZOOM) || 0;
    //console.log('Pitch:', result, '(from:)', zoom);

    return clamp(result, MIN_PITCH, MAX_PITCH);
}

function addPitchToConfig(config) {
    const pitch = getPitchFromZoom(config.zoom);
    return { pitch, ...config };
}

function deselect(callbacks) {
    map.setFilter('location-focused', ['==', 'name', '']);
    map.getCanvas().style.cursor = '';
    popupBase.remove();

    callbacks.locationBlur();
}


/**
 * Generate a geojson Feature out of a location data point
 */
export function createGeojsonLocation({ label, lat, link, lon, name }) {
    label, lat, link, lon, name; // eslint-disable-line

    return {
        type: 'Feature',
        properties: { label, name, link },
        geometry: {
            type: 'Point',
            coordinates: [lon, lat],
        },
    };
}


export function loadMap(options, EventBus) {
    $emit = EventBus;

    return new Promise(resolve => {
        // eslint-disable-next-line no-new
        map = window.map = new mapboxgl.Map(Object.assign({}, MAP_CONFIG, options))
            .on('load', function() {
                resolve(map);
            });

        // Create a popup, but don't add it to the map yet.
        popupBase = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            anchor: 'bottom',
        });

        popupActive = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            anchor: 'bottom',
        });
    });
}


export function unloadMap() {
    map = map.remove();
    popupBase = popupBase.remove();
}


export function loadImage({ name, url }) {
    return new Promise((resolve, reject) => {
        map.loadImage(url, (error, image) => {
            error && reject(error);
            map.addImage(name, image);
            resolve(name);
        });
    });
}


export function loadLayers(features) {
    // Add the locations source
    map.addSource('location-data', Object.assign({}, MAP_SOURCE_CONFIG, {
        data: { features },
    }));

    // Add map layers
    MAP_LAYER_CONFIG.forEach(cfg => map.addLayer(cfg));

    return new Promise(resolve => resolve());
}

export function addEvents(callbacks) {
    return new Promise(resolve => {
        // Zoom handler
        map.on('zoom', () => {
            $emit('zoom-changed', map.getZoom());
        });

        map.on('zoomend', debounce(() => {
            const pitch = getPitchFromZoom(getZoom());
            map.easeTo({ pitch });
        }, 100, {}));

        // Click on map (touch devices)
        if (touchevents) {
            map.on('click', () => deselect(callbacks));
        }

        // Cluster handlers
        map.on('click', 'cluster', e => {
            const cluster = e.features[0];
            callbacks.clusterClick(cluster);
        });

        if (!touchevents) {
            map.on('mouseenter', 'cluster', () => {
                map.getCanvas().style.cursor = 'pointer';
            });

            map.on('mouseleave', 'cluster', () => {
                map.getCanvas().style.cursor = '';
            });
        }

        // Location handlers
        map.on('click', 'location-focused', e => {
            const location = e.features[0];
            callbacks.locationClick(location);
        });

        map.on('mouseleave', 'location-focused', () => deselect(callbacks));

        map.on(touchevents ? 'click' : 'mouseenter', 'location', e => {
            const location = e.features[0];

            map.setFilter('location-focused', ['==', 'name', location.properties.name]);
            map.getCanvas().style.cursor = 'pointer';
            popupBase
                .setLngLat(location.geometry.coordinates)
                .setHTML(location.properties.name)
                .addTo(map);

            callbacks.locationFocus(location);
        });

        resolve();
    });
}

export function setActive(location) {
    const name = get(location, 'properties.name', '');
    map.setFilter('location-active', ['==', 'name', name]);

    if (name) {
        popupActive
            .setLngLat(location.geometry.coordinates)
            .setHTML(location.properties.name)
            .addTo(map);
    } else {
        popupActive.remove();
    }
}

export function jumpTo(config) {
    // NOTE: `map.jumpTo` doesn't support `offset` so we sneak it in via `map.easeTo`
    const options = addPitchToConfig({
        ...config,
        duration: 0,
    });

    popupBase.remove();
    map.easeTo(options);
}

export function flyTo(config) {
    const options = addPitchToConfig(config);
    popupBase.remove();
    map.flyTo(options);
}

export function easeTo(config) {
    const options = addPitchToConfig(config);
    popupBase.remove();
    map.easeTo(options);
}

export function fitBounds(bounds, config) {
    const options = addPitchToConfig(config);
    map.fitBounds(bounds, options);
}

export function zoomBy(change) {
    easeTo({
        zoom: Math.round(map.getZoom()) + change,
    });
}

export function zoomTo(level) {
    easeTo({
        zoom: level,
    });
}

export function getZoom() {
    return map.getZoom();
}



// WEBPACK FOOTER //
// ./vehi-industriepfad/assets/src/js/app/framework/components/mapbox-map/helpers.js